import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/UIKit";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function PortalSaker() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => setItems((await api.get("/portal/cases")).data);
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="portal-saker-page">
      <PageHeader
        title="Mine saker"
        subtitle="Send inn nye henvendelser eller se status på tidligere saker"
        actions={
          <button onClick={() => setOpen(true)}
            className="h-9 px-3.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-2"
            data-testid="portal-ny-sak">
            <Plus className="h-4 w-4" /> Ny henvendelse
          </button>
        }
      />

      <Card className="overflow-hidden">
        {!items ? <div className="p-8"><Spinner /></div>
          : items.length === 0 ? <Empty title="Ingen saker" subtitle="Alt ser bra ut!" />
          : (
          <div className="divide-y divide-zinc-900">
            {items.map((c) => (
              <div key={c.id} className="p-4 hover:bg-zinc-900/30">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-emerald-400">{c.case_number}</span>
                      <span className="text-zinc-100 font-medium">{c.subject}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{c.category} · {fmtDate(c.created_at)}</div>
                    {c.description && <div className="text-xs text-zinc-400 mt-2 line-clamp-2">{c.description}</div>}
                  </div>
                  <div className="flex gap-2">
                    <StatusBadge value={c.priority} />
                    <StatusBadge value={c.status} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <NyPortalSak open={open} onOpenChange={setOpen} onCreated={() => { setOpen(false); load(); }} />
    </div>
  );
}

function NyPortalSak({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ subject: "", description: "", category: "generell" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/portal/cases", form);
      toast.success("Henvendelse sendt inn"); onCreated();
    } catch { toast.error("Feilet"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border-zinc-900">
        <DialogHeader>
          <DialogTitle className="font-heading">Ny henvendelse</DialogTitle>
          <DialogDescription className="sr-only">Kontakt kundeservice</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <input required placeholder="Emne" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm" data-testid="portal-sak-subject" />
          <textarea rows={4} placeholder="Beskriv henvendelsen…" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2 rounded-md bg-zinc-900 border border-zinc-800 text-sm" data-testid="portal-sak-desc" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
            className="w-full h-10 px-3 rounded-md bg-zinc-900 border border-zinc-800 text-sm">
            {["generell", "faktura", "forbruk", "avtale", "teknisk", "betaling"].map((c) => <option key={c}>{c}</option>)}
          </select>
          <button type="submit" disabled={busy} className="w-full h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-medium disabled:opacity-60 flex items-center justify-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Send inn
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
